import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

/** Split a comma-separated string into trimmed, non-empty, de-duplicated items. */
export const parseCommaList = (raw: string): string[] =>
  Array.from(new Set(raw.split(',').map(s => s.trim()).filter(Boolean)));

type CommaListInputProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string[] | undefined;
  onChange: (next: string[]) => void;
};

/**
 * Comma-separated list editor for string[] fields (SOAP template select /
 * checklist options, etc).
 *
 * Why this exists: a TextField whose `value` is derived from the PARSED array
 * (`options.join(', ')`) and whose onChange re-parses on every keystroke can
 * never show a trailing comma — "A," parses to ["A"], the parent stores that,
 * and React rewrites the input to "A" on the same event, so the comma looks
 * un-typeable. This component keeps the RAW draft string as the visible value
 * and still hands the parent the parsed array on every change (live previews
 * keep updating). The draft is re-seeded only when the incoming array stops
 * matching the draft's normalized form — i.e. the parent swapped in a
 * different field (reorder/delete reuse the same instance) — never mid-keystroke.
 */
export default function CommaListInput({ value, onChange, onBlur, ...rest }: CommaListInputProps) {
  const joined = (value || []).join(', ');
  const [draft, setDraft] = useState(joined);

  useEffect(() => {
    if (parseCommaList(draft).join(', ') !== joined) setDraft(joined);
    // Only react to the parent's array changing; `draft` is deliberately not a
    // dependency or the effect would fight every keystroke. (No react-hooks
    // eslint plugin in this CRA config, so no disable directive is needed.)
  }, [joined]);

  return (
    <TextField
      {...rest}
      value={draft}
      onChange={e => {
        setDraft(e.target.value);
        onChange(parseCommaList(e.target.value));
      }}
      onBlur={e => {
        setDraft(parseCommaList(draft).join(', '));
        onBlur?.(e);
      }}
    />
  );
}
